/**
 * Section Form Modal Component
 * Modal form for creating or editing a section
 */

import React, { useEffect, useState } from 'react';
import { OutletSection } from '../../../types/section';
import { Input, Modal } from '../../common';
import { Button } from '../../common/Button';

interface SectionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, isHidden: boolean) => void;
  section?: OutletSection | null;
  isLoading?: boolean;
}

export const SectionFormModal: React.FC<SectionFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  section,
  isLoading = false,
}) => {
  const [name, setName] = useState('');
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    if (section) {
      setName(section.name);
      setIsHidden(section.is_hidden);
    } else {
      setName('');
      setIsHidden(false);
    }
  }, [section, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit(name.trim(), isHidden);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={section ? 'Edit Section' : 'Add Section'}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Section Name"
          value={name}
          onChange={(val) => setName(String(val))}
          placeholder="e.g. BALCONY, RESTAURANT, BAR"
          required
        />

        <label
          htmlFor="section-hidden-toggle"
          className="flex items-center gap-3 cursor-pointer"
        >
          <div className="relative">
            <input
              id="section-hidden-toggle"
              type="checkbox"
              checked={isHidden}
              onChange={(e) => setIsHidden(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-red-400 transition-colors" />
            <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm peer-checked:translate-x-5 transition-transform" />
          </div>
          <span className="text-sm text-gray-700">Hidden</span>
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" size="sm" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            type="submit"
            isLoading={isLoading}
            disabled={!name.trim()}
          >
            {section ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
